import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MoreHorizontal,
  Pencil,
  Power,
  Share2,
  Trash2,
  LogIn,
  User as UserIcon,
  Mail,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { userApi } from "@/lib/api/admin-api";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import type { User } from "@/types/user";
import { UserFormDialog } from "./UserFormDialog";

function InfoPill({
  icon: Icon,
  value,
}: {
  icon: React.ElementType;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="size-4 text-(--color-text-secondary)" />
      <span className="text-sm text-(--color-text-secondary)">{value}</span>
    </div>
  );
}

export function UserCard({
  user,
  canImpersonate = false,
  canManageActions = true,
  onLoginAs,
}: {
  user: User;
  canImpersonate?: boolean;
  canManageActions?: boolean;
  onLoginAs?: (target: User) => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const fullName =
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;

  const primaryRole =
    user.userRoles?.find((ur) => ur.role.code !== "SUPER_ADMIN") ??
    user.userRoles?.[0];

  const toggleActiveMutation = useMutation({
    mutationFn: () =>
      userApi.update(user.id, { isActive: !user.isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["users", "stats"] });
      toast.success(
        user.isActive ? t`User set as inactive` : t`User set as active`,
      );
    },
    onError: () => toast.error(t`Failed to update user status`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => userApi.delete(user.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["users", "stats"] });
      toast.success(t`User removed`);
    },
    onError: () => toast.error(t`Failed to remove user`),
  });

  const handleShare = () => {
    const url = `${window.location.origin}/users/${user.id}`;
    void navigator.clipboard.writeText(url).then(() => {
      toast.success(t`Link copied to clipboard`);
    });
  };

  return (
    <>
      <div className="border border-(--color-border) rounded-[10px] overflow-hidden bg-(--color-surface-muted)">
        <div className="flex items-center gap-3 p-3">
          {/* Avatar */}
          <div className="size-12 bg-(--color-surface-content) border border-(--color-border) rounded-[10px] flex items-center justify-center shrink-0">
            <UserIcon className="size-5 text-(--color-text-secondary)" />
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
                {fullName}
              </span>
              <span
                className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                  user.isActive
                    ? "bg-(--color-status-active) text-(--brand-white)"
                    : "bg-(--color-surface-accent) text-(--color-text-secondary)",
                )}
              >
                {user.isActive ? t`Active` : t`Inactive`}
              </span>
              {primaryRole && (
                <span className="font-mono text-xs text-(--color-text-secondary)">
                  {primaryRole.role.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <InfoPill icon={Mail} value={user.email} />
              {user.company && (
                <InfoPill icon={Building2} value={user.company.name} />
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="size-12 bg-(--color-surface-content) border border-(--color-border) rounded-[10px] flex items-center justify-center hover:bg-(--color-surface-muted) transition-colors"
                >
                  <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  onSelect={() => setEditOpen(true)}
                >
                  <Pencil className="size-4" />
                  {t`Edit`}
                </DropdownMenuItem>
                {canManageActions ? (
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer"
                    disabled={toggleActiveMutation.isPending}
                    onSelect={() => toggleActiveMutation.mutate()}
                  >
                    <Power className="size-4" />
                    {user.isActive ? t`Set as inactive` : t`Set as active`}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  onSelect={handleShare}
                >
                  <Share2 className="size-4" />
                  {t`Share`}
                </DropdownMenuItem>
                {canImpersonate && onLoginAs ? (
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer"
                    onSelect={() => onLoginAs(user)}
                  >
                    <LogIn className="size-4" />
                    {t`Login as`}
                  </DropdownMenuItem>
                ) : null}
                {canManageActions ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="gap-2 cursor-pointer text-(--color-danger-text) focus:text-(--color-danger-text)"
                      onSelect={() => setRemoveOpen(true)}
                    >
                      <Trash2 className="size-4" />
                      {t`Remove`}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              className="h-12 gap-2 rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) px-4"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-4" />
              {t`Edit user`}
            </Button>
          </div>
        </div>
      </div>

      <UserFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={user}
      />

      <ConfirmDeleteDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title={t`Remove user?`}
        description={t`This will permanently remove ${fullName} from the system.`}
        bullets={[t`This action cannot be undone`]}
        confirmLabel={t`Remove user`}
        onConfirm={() => deleteMutation.mutate()}
        isPending={deleteMutation.isPending}
      />
    </>
  );
}
